import React, { useState, useEffect } from 'react';
import { 
    collection, 
    query, 
    orderBy, 
    limit, 
    getDocs, 
    getDoc,
    where, 
    Timestamp, 
    deleteDoc, 
    doc,
    startAfter
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { StorageFile } from '../../types/storage';
import { useNotification } from '../../contexts/NotificationContext';

const AdminUploads: React.FC = () => {
    const [files, setFiles] = useState<StorageFile[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterType, setFilterType] = useState<'all' | 'image' | 'media' | 'file'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
    const [lastVisible, setLastVisible] = useState<any>(null);
    const [pageStack, setPageStack] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const { showSuccess, showError } = useNotification();

    const fetchFiles = async (direction: 'next' | 'prev' | 'first' = 'first') => {
        try {
            setIsLoading(true);
            setSelectedFiles(new Set()); // Reset selections on page change
            
            let q = query(
                collection(db, 'storageFiles'),
                orderBy('createdAt', 'desc'),
                limit(100)
            );

            let newStack = [...pageStack];
            let newPage = currentPage;

            if (direction === 'next' && lastVisible) {
                q = query(q, startAfter(lastVisible));
                newStack.push(lastVisible);
                newPage++;
            } else if (direction === 'prev' && pageStack.length > 0) {
                newStack.pop(); 
                if (newStack.length > 0) {
                    const prevCursor = newStack[newStack.length - 1];
                    q = query(q, startAfter(prevCursor));
                }
                newPage--;
            } else {
                newStack = [];
                newPage = 1;
            }

            const snapshot = await getDocs(q);
            const newFiles = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as StorageFile);
            
            setFiles(newFiles);
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            setHasMore(newFiles.length === 100);
            setPageStack(newStack);
            setCurrentPage(newPage);

        } catch (error: any) {
            console.error('Error fetching files:', error);
            showError('Failed to load uploads: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Auto flip to page 1 when filter/search changes
    useEffect(() => {
        fetchFiles('first');
    }, [filterType, searchTerm]);

    const toggleSelect = (id: string) => {
        const newSelected = new Set(selectedFiles);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedFiles(newSelected);
    };

    const handleDelete = async (file: StorageFile) => {
        if (!window.confirm('Are you sure you want to permanently delete this file?')) return;
        
        try {
            // Delete from storage
            const url = file.url;
            if (url) {
                const storageRef = ref(storage, url);
                await deleteObject(storageRef).catch(e => console.warn('Storage delete failed:', e));
            }
            
            // Delete from Firestore
            await deleteDoc(doc(db, 'storageFiles', file.id!));
            
            setFiles(prev => prev.filter(f => f.id !== file.id));
            showSuccess('File deleted successfully');
        } catch (error: any) {
            showError('Failed to delete file: ' + error.message);
        }
    };

    const handleBulkDelete = async () => {
        const count = selectedFiles.size;
        if (count === 0) return;
        if (!window.confirm(`Are you sure you want to permanently delete ${count} selected files?`)) return;

        setIsLoading(true);
        try {
            const filesToDelete = files.filter(f => selectedFiles.has(f.id!));
            for (const file of filesToDelete) {
                if (file.url) {
                    const storageRef = ref(storage, file.url);
                    await deleteObject(storageRef).catch(() => {});
                }
                await deleteDoc(doc(db, 'storageFiles', file.id!));
            }
            
            setFiles(prev => prev.filter(f => !selectedFiles.has(f.id!)));
            setSelectedFiles(new Set());
            showSuccess(`${count} files deleted successfully`);
        } catch (error: any) {
            showError('Bulk delete errors occurred');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleSelectAll = () => {
        if (selectedFiles.size === filteredFiles.length) {
            setSelectedFiles(new Set());
        } else {
            setSelectedFiles(new Set(filteredFiles.map(f => f.id!)));
        }
    };

    const [selectedFile, setSelectedFile] = useState<StorageFile | null>(null);
    const [uploaderDetails, setUploaderDetails] = useState<{ email?: string; name?: string; id?: string } | null>(null);
    const [isFetchingUploader, setIsFetchingUploader] = useState(false);

    const fetchUploader = async (userId: string) => {
        setIsFetchingUploader(true);
        setUploaderDetails(null);
        try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
                const userData = userDoc.data();
                setUploaderDetails({
                    id: userId,
                    email: userData.email,
                    name: userData.displayName || userData.firstName || 'Unknown Name'
                });
            } else {
                setUploaderDetails({ id: userId, email: 'user-not-found@temp.com', name: 'Deleted User' });
            }
        } catch (error) {
            console.error('Error fetching uploader:', error);
        } finally {
            setIsFetchingUploader(false);
        }
    };

    const handleFileClick = (file: StorageFile) => {
        setSelectedFile(file);
        fetchUploader(file.userId);
    };

    const filteredFiles = files.filter(f => {
        // Filter out trashed
        if (f.isTrashed) return false;

        // Filter by type
        if (filterType !== 'all' && f.type !== filterType) return false;

        // Filter by search
        const searchLower = searchTerm.toLowerCase();
        const nameMatch = (f.originalName || '').toLowerCase().includes(searchLower);
        const userMatch = (f.userId || '').toLowerCase().includes(searchLower);
        
        return nameMatch || userMatch;
    });

    return (
        <div className="p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen relative overflow-hidden">
            {/* Header / Stats */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">User Uploads</h2>
                    <p className="text-gray-500 dark:text-gray-400">Monitor and moderate content uploaded by users</p>
                </div>
                
                <div className="flex items-center gap-3">
                    <button 
                        onClick={toggleSelectAll}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 transition-colors"
                    >
                        {selectedFiles.size === filteredFiles.length && filteredFiles.length > 0 ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedFiles.size > 0 && (
                        <button 
                            onClick={handleBulkDelete}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shadow-sm"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete {selectedFiles.size} Selected
                        </button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                <div className="flex-1 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </span>
                    <input 
                        type="text" 
                        placeholder="Search by filename or User ID..." 
                        className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border-none rounded-lg focus:ring-2 focus:ring-red-500 text-gray-900 dark:text-white"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                
                <div className="flex gap-2 bg-gray-50 dark:bg-gray-700 p-1 rounded-lg">
                    {(['all', 'image', 'media', 'file'] as const).map((type) => (
                        <button
                            key={type}
                            onClick={() => setFilterType(type)}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                                filterType === type 
                                ? 'bg-white dark:bg-gray-600 text-red-600 dark:text-red-400 shadow-sm' 
                                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                            }`}
                        >
                            {type.charAt(0) + type.slice(1)}s
                        </button>
                    ))}
                </div>
            </div>

            {/* Gallery Grid */}
            {isLoading && files.length === 0 ? (
                <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
                </div>
            ) : filteredFiles.length === 0 ? (
                <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h14a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2-2v12a2 2 0 002 2z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No uploads found</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Try adjusting your search or filters.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-10 gap-3">
                    {filteredFiles.map((file) => (
                        <div 
                            key={file.id} 
                            className={`relative group rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                                selectedFiles.has(file.id!) 
                                ? 'border-red-500 ring-2 ring-red-500/20 shadow-lg' 
                                : selectedFile?.id === file.id
                                ? 'border-blue-500 shadow-lg'
                                : 'border-transparent bg-white dark:bg-gray-800 shadow-sm hover:shadow-md'
                            }`}
                            onClick={() => handleFileClick(file)}
                        >
                            {/* Checkbox for selection */}
                            <div className="absolute top-2 left-2 z-10" onClick={(e) => e.stopPropagation()}>
                                <input 
                                    type="checkbox" 
                                    checked={selectedFiles.has(file.id!)}
                                    onChange={() => toggleSelect(file.id!)}
                                    className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                                />
                            </div>

                            {/* Preview Area */}
                            <div className="aspect-square bg-gray-100 dark:bg-gray-700 overflow-hidden flex items-center justify-center">
                                {file.type === 'image' ? (
                                    <img 
                                        src={file.url} 
                                        alt={file.originalName} 
                                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                        loading="lazy"
                                    />
                                ) : (
                                    <div className="flex flex-col items-center p-4">
                                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span className="text-[10px] mt-2 font-mono text-gray-400 truncate w-full text-center">{file.mimeType}</span>
                                    </div>
                                )}
                            </div>

                            {/* Info Area */}
                            <div className="p-2 space-y-1">
                                <div className="text-[11px] font-medium text-gray-900 dark:text-white truncate" title={file.originalName}>
                                    {file.originalName}
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] text-gray-400 font-mono">
                                        {Math.round(file.size / 1024)} KB
                                    </span>
                                </div>
                                <div className="text-[10px] text-gray-400">
                                    {file.createdAt?.toDate ? file.createdAt.toDate().toLocaleDateString() : 'No date'}
                                </div>
                                <div className="text-[9px] text-gray-300 dark:text-gray-600 truncate">
                                    UID: {file.userId}
                                </div>
                            </div>

                            {/* Hover Overlay */}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                                <div className="flex gap-2 pointer-events-auto">
                                    <a 
                                        href={file.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="p-2 bg-white rounded-full shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-all hover:scale-110"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination Controls */}
            {filteredFiles.length > 0 && (
                <div className="flex items-center justify-between bg-white dark:bg-gray-800 px-6 py-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mt-6">
                    <div className="flex flex-1 justify-between sm:hidden">
                        <button 
                            onClick={() => fetchFiles('prev')} 
                            disabled={currentPage === 1 || isLoading}
                            className="relative inline-flex items-center rounded-md bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <button 
                            onClick={() => fetchFiles('next')} 
                            disabled={!hasMore || isLoading}
                            className="relative ml-3 inline-flex items-center rounded-md bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700 dark:text-gray-400">
                                Showing page <span className="font-bold text-gray-900 dark:text-white">{currentPage}</span> 
                                <span className="mx-1 text-gray-400">|</span> 
                                <span className="text-gray-500">{filteredFiles.length} items shown</span>
                            </p>
                        </div>
                        <div>
                            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                <button
                                    onClick={() => fetchFiles('first')}
                                    disabled={currentPage === 1 || isLoading}
                                    className="relative inline-flex items-center rounded-l-md px-3 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                                >
                                    <span className="sr-only">First</span>
                                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M15.79 14.77a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.04 1.08L11.832 10l3.938 3.71a.75.75 0 01.02 1.06zm-6 0a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.04 1.08L5.832 10l3.938 3.71a.75.75 0 01.02 1.06z" clipRule="evenodd" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => fetchFiles('prev')}
                                    disabled={currentPage === 1 || isLoading}
                                    className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => fetchFiles('next')}
                                    disabled={!hasMore || isLoading}
                                    className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                                >
                                    Next
                                </button>
                                <div className="relative inline-flex items-center rounded-r-md px-3 py-2 text-gray-300 dark:text-gray-600 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 cursor-default">
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                </div>
                            </nav>
                        </div>
                    </div>
                </div>
            )}

            {/* Slide-over Details Panel */}
            <div 
                className={`fixed inset-y-0 right-0 w-96 bg-white dark:bg-gray-800 shadow-2xl z-[60] transform transition-transform duration-300 ease-in-out border-l border-gray-200 dark:border-gray-700 ${
                    selectedFile ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                {selectedFile && (
                    <div className="h-full flex flex-col">
                        {/* Header */}
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                            <h3 className="font-semibold text-gray-900 dark:text-white">File Details</h3>
                            <button 
                                onClick={() => setSelectedFile(null)}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-6">
                            {/* Preview */}
                            <div className="bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden aspect-video flex items-center justify-center relative group">
                                {selectedFile.type === 'image' ? (
                                    <img src={selectedFile.url} alt="" className="max-w-full max-h-full object-contain" />
                                ) : (
                                    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                )}
                            </div>

                            {/* Uploader Section */}
                            <div className="space-y-3">
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Uploaded By</h4>
                                {isFetchingUploader ? (
                                    <div className="flex items-center space-x-3 animate-pulse">
                                        <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                                        <div className="space-y-2">
                                            <div className="w-24 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                            <div className="w-32 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                        </div>
                                    </div>
                                ) : uploaderDetails ? (
                                    <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-100 dark:border-gray-700">
                                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center font-bold">
                                            {uploaderDetails.name?.charAt(0)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{uploaderDetails.name}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{uploaderDetails.email}</p>
                                        </div>
                                        <a 
                                            href={`/admin/control-panel-7x9k2m/user-details/${uploaderDetails.id}`}
                                            className="p-1.5 hover:bg-white dark:hover:bg-gray-600 rounded-lg shadow-sm transition-all"
                                            title="View User Profile"
                                        >
                                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                        </a>
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500">Uploader information unavailable</p>
                                )}
                            </div>

                            {/* Metadata Section */}
                            <div className="space-y-4">
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">File Metadata</h4>
                                <div className="grid grid-cols-1 gap-4 text-sm">
                                    <div className="space-y-1">
                                        <p className="text-gray-500 dark:text-gray-400 text-xs">Original Filename</p>
                                        <p className="font-medium text-gray-900 dark:text-white break-all">{selectedFile.originalName}</p>
                                    </div>
                                    <div className="flex justify-between border-b dark:border-gray-700 pb-2">
                                        <span className="text-gray-500 dark:text-gray-400">File Type</span>
                                        <span className="font-medium text-gray-900 dark:text-white uppercase">{selectedFile.type}</span>
                                    </div>
                                    <div className="flex justify-between border-b dark:border-gray-700 pb-2">
                                        <span className="text-gray-500 dark:text-gray-400">File Size</span>
                                        <span className="font-medium text-gray-900 dark:text-white">{Math.round(selectedFile.size / 1024)} KB</span>
                                    </div>
                                    <div className="flex justify-between border-b dark:border-gray-700 pb-2">
                                        <span className="text-gray-500 dark:text-gray-400">Upload Date</span>
                                        <span className="font-medium text-gray-900 dark:text-white">{selectedFile.createdAt?.toDate ? selectedFile.createdAt.toDate().toLocaleString() : 'N/A'}</span>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-gray-500 dark:text-gray-400 text-xs">Internal Path</p>
                                        <p className="font-mono text-[10px] text-gray-400 break-all bg-gray-50 dark:bg-gray-900 p-2 rounded">
                                            {selectedFile.url.split('?')[0].split('/o/')[1]?.replace(/%2F/g, '/')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer / Actions */}
                        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 space-y-3">
                            <button 
                                onClick={() => {
                                    navigator.clipboard.writeText(selectedFile.url);
                                    showSuccess('URL copied to clipboard');
                                }}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-all font-medium shadow-sm"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                </svg>
                                Copy Asset URL
                            </button>
                            <button 
                                onClick={() => {
                                    handleDelete(selectedFile);
                                    setSelectedFile(null);
                                }}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 border border-red-100 rounded-xl hover:bg-red-100 transition-all font-medium"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete Permanently
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Backdrop for SlideOver */}
            {selectedFile && (
                <div 
                    className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-[55] transition-opacity duration-300"
                    onClick={() => setSelectedFile(null)}
                />
            )}
        </div>
    );
};

export default AdminUploads;
